import type { CSSProperties } from 'react';
import {
  CROSS_AXIS_OVERFLOW,
  CSCROLLABLE_CONTAIN_LAYOUT,
  CSCROLLABLE_WILL_CHANGE_SCROLL_POSITION,
  SCROLL_DIRECTION,
} from './Cscrollable.constants';
import type { CrossAxisOverflow, ScrollDirection } from './Cscrollable.types';

interface GetScrollableStyleInput {
  direction: ScrollDirection;
  crossAxisOverflow: CrossAxisOverflow;
  crossAxisBleed: number;
  fadeSize: number;
  firstItemSpacing: CSSProperties['paddingTop'];
  style?: CSSProperties;
}

interface GetScrollableAxisStyleInput {
  direction: ScrollDirection;
  crossAxisOverflow: CrossAxisOverflow;
}

interface GetScrollableCrossAxisBleedStyleInput {
  direction: ScrollDirection;
  crossAxisBleed: number;
}

interface GetScrollablePaddingStyleInput {
  direction: ScrollDirection;
  fadeSize: number;
  firstItemSpacing: CSSProperties['paddingTop'];
}

const CSCROLLABLE_BASE_STYLE: CSSProperties = {
  display: 'flex',
  flex: 1,
  minHeight: 0,
  minWidth: 0,
  width: '100%',
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  maskSize: '100% 100%',
  WebkitMaskSize: '100% 100%',
  maskRepeat: 'no-repeat',
  WebkitMaskRepeat: 'no-repeat',
  WebkitOverflowScrolling: 'touch',
  contain: CSCROLLABLE_CONTAIN_LAYOUT,
  willChange: CSCROLLABLE_WILL_CHANGE_SCROLL_POSITION,
};

function getScrollableAxisStyle({
  direction,
  crossAxisOverflow,
}: GetScrollableAxisStyleInput): CSSProperties {
  if (direction === SCROLL_DIRECTION.Vertical) {
    return {
      overflowX: crossAxisOverflow,
      overflowY: CROSS_AXIS_OVERFLOW.Auto,
      flexDirection: 'column',
    };
  }

  return {
    overflowX: CROSS_AXIS_OVERFLOW.Auto,
    overflowY: crossAxisOverflow,
    flexDirection: 'row',
  };
}

function getScrollablePaddingStyle({
  direction,
  fadeSize,
  firstItemSpacing,
}: GetScrollablePaddingStyleInput): CSSProperties {
  if (direction === SCROLL_DIRECTION.Vertical) {
    return {
      paddingTop: firstItemSpacing,
      paddingLeft: 0,
      paddingBottom: `${fadeSize}px`,
      paddingRight: 0,
    };
  }

  return {
    paddingTop: 3,
    paddingLeft: firstItemSpacing,
    paddingBottom: 0,
    paddingRight: `${fadeSize}px`,
  };
}

/**
 * Cross-axis bleed gutter: pads the scroll element on the axis perpendicular to
 * `direction` by `crossAxisBleed` px (pushing the overflow clip boundary outward
 * so hover-scale / morph bleed stays visible) and pulls it back with an equal
 * negative margin so the layout footprint is unchanged. Emitted as longhand and
 * merged AFTER the consumer style so it composes on the intended cross-axis edges
 * without a consumer `padding` shorthand silently dropping it.
 *
 * WHY THE VERTICAL BRANCH RELEASES `width`. "Footprint unchanged" only holds if
 * the negative margins are what set the box's cross size — and the base style
 * pins `width: '100%'` under `box-sizing: border-box`, so with a bleed the box
 * stays exactly one column wide, the two negative margins slide it a full bleed
 * to the left, and the two paddings eat `2 x crossAxisBleed` out of the CONTENT
 * box. A bleeding pane's content then sits one bleed short on its trailing edge
 * and misaligns with any sibling laid out against the same column — measured as
 * 24px on the team modal's desktop panes. Releasing the cross size lets the
 * stretch resolve against the margin box instead, which is the arrangement the
 * paragraph above describes. The horizontal branch releases nothing because the
 * base style pins no `height` for it to undo, and clearing a consumer's own
 * cross size is not this helper's call.
 */
function getScrollableCrossAxisBleedStyle({
  direction,
  crossAxisBleed,
}: GetScrollableCrossAxisBleedStyleInput): CSSProperties {
  if (crossAxisBleed <= 0) {
    return {};
  }

  if (direction === SCROLL_DIRECTION.Vertical) {
    return {
      width: 'auto',
      paddingLeft: crossAxisBleed,
      paddingRight: crossAxisBleed,
      marginLeft: -crossAxisBleed,
      marginRight: -crossAxisBleed,
    };
  }

  return {
    paddingTop: crossAxisBleed,
    paddingBottom: crossAxisBleed,
    marginTop: -crossAxisBleed,
    marginBottom: -crossAxisBleed,
  };
}

export function getScrollableStyle({
  direction,
  crossAxisOverflow,
  crossAxisBleed,
  fadeSize,
  firstItemSpacing,
  style,
}: GetScrollableStyleInput): CSSProperties {
  return {
    ...CSCROLLABLE_BASE_STYLE,
    ...getScrollableAxisStyle({ direction, crossAxisOverflow }),
    ...getScrollablePaddingStyle({ direction, fadeSize, firstItemSpacing }),
    ...style,
    ...getScrollableCrossAxisBleedStyle({ direction, crossAxisBleed }),
  };
}
